import { useEffect, useState, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { CloudOff, RotateCcw, Trash2, X, RefreshCw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { db } from '@/lib/offline/db';
import { syncManager, useSyncManagerStore } from '@/lib/offline/sync-manager';
import { useOnlineStatus } from '@/lib/offline/online-status';
import type { QueueEntry } from '@/lib/offline/types';

const RESOURCE_LABEL: Record<string, string> = {
  'safety.incident.create': 'Safety incident',
  'safety.inspection.create': 'Inspection',
  'safety.hazard.create': 'Hazard observation',
  'safety.corrective-action.create': 'Corrective action',
  'safety.corrective-action.update-status': 'Corrective action status',
  'workforce.attendance.create-session': 'Attendance session',
  'workforce.attendance.check-in': 'Check-in',
  'workforce.attendance.check-out': 'Check-out',
  'workforce.attendance.close-session': 'Close session',
  'operations.meu.create': 'MEU creation',
  'operations.custody-transfer.create': 'Custody transfer',
};

interface SyncDrawerProps {
  trigger: React.ReactNode;
}

export function SyncDrawer({ trigger }: SyncDrawerProps) {
  const [open, setOpen] = useState(false);
  const isOnlineNow = useOnlineStatus();
  const isSyncing = useSyncManagerStore((s) => s.isSyncing);

  const pending = useLiveQuery(
    () =>
      db.queue
        .where('status')
        .anyOf(['pending', 'in-flight'])
        .toArray()
        .then((rows) => rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt))),
    [],
  );
  const failed = useLiveQuery(
    () =>
      db.queue
        .where('status')
        .anyOf(['failed', 'requires-attention'])
        .toArray()
        .then((rows) => rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))),
    [],
  );

  // Re-render the badge counts even when the drawer is closed
  useEffect(() => {
    // useLiveQuery handles subscription; this is just to keep ESLint happy
  }, [pending, failed]);

  const handleSyncNow = useCallback(async () => {
    await syncManager.syncNow();
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="w-[420px] sm:w-[460px] p-0 sm:max-w-[460px]">
        <div className="p-6 pb-3">
          <SheetHeader>
            <SheetTitle className="text-lg flex items-center gap-2">
              <CloudOff className="size-4 text-muted-foreground" />
              Offline sync
            </SheetTitle>
            <p className="text-sm text-text-body">
              {isOnlineNow ? 'Online' : 'Offline'} · records queued on this
              device until they reach the server.
            </p>
          </SheetHeader>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncNow}
              disabled={!isOnlineNow || isSyncing}
            >
              <RefreshCw
                className={`size-3.5 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`}
              />
              {isSyncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </div>
        </div>
        <Separator />
        <Tabs defaultValue="pending" className="px-4 pt-3">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="pending">
              Pending
              {pending && pending.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[10px]">
                  {pending.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="failed">
              Failed
              {failed && failed.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-[10px]">
                  {failed.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending">
            <ScrollArea className="h-[calc(100vh-260px)] pr-2">
              <EntryList entries={pending ?? []} emptyText="No pending records." />
            </ScrollArea>
          </TabsContent>
          <TabsContent value="failed">
            <ScrollArea className="h-[calc(100vh-260px)] pr-2">
              <EntryList
                entries={failed ?? []}
                emptyText="Nothing to fix."
                showActions
              />
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function EntryList({
  entries,
  emptyText,
  showActions,
}: {
  entries: QueueEntry[];
  emptyText: string;
  showActions?: boolean;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-text-body text-center py-12">{emptyText}</p>
    );
  }
  return (
    <div className="py-3 space-y-2">
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="rounded-lg border bg-card p-3 text-sm flex items-start justify-between gap-2"
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium text-text-heading text-[13px]">
              {RESOURCE_LABEL[entry.resource] ?? entry.resource}
            </p>
            <p className="text-xs text-text-body mt-0.5">
              {new Date(entry.createdAt).toLocaleString()} · attempt {entry.retries + 1}
            </p>
            {entry.lastError && (
              <p className="text-xs text-destructive mt-1 line-clamp-2">
                {entry.lastError}
              </p>
            )}
          </div>
          {showActions && entry.id !== undefined && (() => {
            const entryId = entry.id;
            return (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => void syncManager.retryEntry(entryId)}
                  title="Retry"
                >
                  <RotateCcw className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  onClick={() => void syncManager.discardEntry(entryId)}
                  title="Discard"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}

// Re-export X for any consumer that needs the close icon
export { X };
